import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { useTranslation } from "react-i18next";
import LoginPage from "./pages/LoginPage";
import ForgetPasswordPage from "./pages/ForgetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import SuccessPage from "./pages/SuccessPage";
import SignatureGatewayPage from "./pages/SignatureGatewayPage";
import ExternalSignerPage from "./pages/ExternalSignerPage";
import GuestLoginPage from "./pages/GuestLoginPage";
import LegacyRedirect from "./components/LegacyRedirect";
import RequireAuth from "./components/RequireAuth";
import SectionWorkspacePage from "./pages/SectionWorkspacePage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import SignYourselfPage from "./pages/SignYourselfPage";
import RequestSignPage from "./pages/RequestSignPage";
import TemplateCreatePage from "./pages/TemplateCreatePage";
import IndividualSigningPage from "./pages/IndividualSigningPage";
import SignedDocumentSummaryPage from "./pages/SignedDocumentSummaryPage";
import InternalSignerPage from "./pages/InternalSignerPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ResetPasswordSuccessPage from "./pages/ResetPasswordSuccessPage";
import CertificateManagementPage from "./pages/admin/CertificateManagementPage";
import { applyPreferredLanguage } from "./i18n";
import { Parse } from "./lib/parse";
import { getSessionToken, persistExtendedUser } from "./lib/auth";
import { AlertProvider } from "./contexts/AlertContext";
import { useBrandingConfig } from "./hooks/useBrandingConfig";

const DEFAULT_TENANT_THEME_COLOR = "#ef4b26";

const isValidHexColor = (value) => /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(String(value || "").trim());

const normalizeHexColor = (value, fallback = DEFAULT_TENANT_THEME_COLOR) => {
  const raw = String(value || "").trim();
  if (!isValidHexColor(raw)) {
    return fallback;
  }
  if (raw.length === 4) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return raw.toLowerCase();
};

const darkenHexColor = (hexColor, factor = 0.14) => {
  const normalized = normalizeHexColor(hexColor);
  const r = Math.max(0, Math.min(255, Math.round(parseInt(normalized.slice(1, 3), 16) * (1 - factor))));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(normalized.slice(3, 5), 16) * (1 - factor))));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(normalized.slice(5, 7), 16) * (1 - factor))));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b
    .toString(16)
    .padStart(2, "0")}`;
};

const applyTenantBrandColor = (color) => {
  if (typeof document === "undefined") {
    return;
  }
  const normalized = normalizeHexColor(color);
  document.documentElement.style.setProperty("--app-brand", normalized);
  document.documentElement.style.setProperty("--app-brand-dark", darkenHexColor(normalized));
};

const readTenantBrandColorFromStorage = () => {
  try {
    const extClassRaw = localStorage.getItem("Extand_Class");
    const extClass = extClassRaw ? JSON.parse(extClassRaw) : null;
    const tenant = Array.isArray(extClass) ? extClass?.[0]?.TenantId || {} : {};
    return (
      tenant?.PrimaryColor ||
      tenant?.primaryColor ||
      tenant?.ThemeColor ||
      tenant?.themeColor ||
      DEFAULT_TENANT_THEME_COLOR
    );
  } catch (error) {
    return DEFAULT_TENANT_THEME_COLOR;
  }
};

const parseStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const toPlainObject = (value) => (typeof value?.toJSON === "function" ? value.toJSON() : value);

const extractProfileLanguage = (extUser) => {
  if (!extUser || typeof extUser !== "object") {
    return "";
  }
  const candidates = [
    extUser?.Language,
    extUser?.language,
    extUser?.attributes?.Language,
    extUser?.attributes?.language
  ];
  const valid = candidates.find((item) => typeof item === "string" && item.trim());
  return valid ? valid.trim() : "";
};

const resolveStoredProfileLanguage = () => {
  const userInfo = parseStoredJson("UserInformation");
  const userInfoLanguageCandidates = [
    userInfo?.language,
    userInfo?.Language,
    localStorage.getItem("i18nextLng")
  ];
  const userInfoLanguage = userInfoLanguageCandidates.find(
    (item) => typeof item === "string" && item.trim()
  );
  if (userInfoLanguage) {
    return userInfoLanguage.trim();
  }

  const extClass = parseStoredJson("Extand_Class");
  const extUser = Array.isArray(extClass) ? extClass?.[0] : extClass;
  return extractProfileLanguage(extUser);
};

function AppContent() {
  const { t } = useTranslation();
  const basename = "/";
  
  // Get tenantId from localStorage
  const getTenantId = () => {
    try {
      const extClassRaw = localStorage.getItem("Extand_Class");
      const extClass = extClassRaw ? JSON.parse(extClassRaw) : null;
      const tid = Array.isArray(extClass) ? extClass?.[0]?.TenantId?.objectId : null;
      console.log('App.js getTenantId:', tid);
      return tid;
    } catch (error) {
      console.error('App.js getTenantId error:', error);
      return null;
    }
  };
  
  const tenantId = getTenantId();
  console.log('App.js: tenantId =', tenantId);
  const { branding } = useBrandingConfig(tenantId);

  useEffect(() => {
    // Commented out - tenant color should only affect emails, not UI
    // const syncTenantBranding = () => {
    //   applyTenantBrandColor(readTenantBrandColorFromStorage());
    // };

    let cancelled = false;
    const bootstrapLanguage = async () => {
      try {
        const storedLanguage = resolveStoredProfileLanguage();
        const hasAppliedStoredLanguage = storedLanguage
          ? applyPreferredLanguage(storedLanguage)
          : false;

        const sessionToken = getSessionToken();
        if (!sessionToken) {
          return;
        }

        const extUserResponse = await Parse.Cloud.run("getUserDetails");
        if (cancelled) {
          return;
        }
        const extUser = toPlainObject(extUserResponse);
        if (extUser) {
          persistExtendedUser(extUser);
        }
        const profileLanguage = extractProfileLanguage(extUser);
        if (!hasAppliedStoredLanguage && profileLanguage) {
          applyPreferredLanguage(profileLanguage);
        }
      } catch (error) {
        console.error("Language bootstrap warning", error);
      }
    };

    bootstrapLanguage();

    // Commented out - tenant color should only affect emails, not UI
    // syncTenantBranding();
    // window.addEventListener("dana:tenant-brand-updated", syncTenantBranding);
    return () => {
      cancelled = true;
    };
    // return () => {
    //   window.removeEventListener("dana:tenant-brand-updated", syncTenantBranding);
    // };
  }, []);

  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route path="/" element={<LoginPage branding={branding} />} />
        <Route path="/forgetpassword" element={<ForgetPasswordPage />} />
        <Route path="/resetpassword" element={<ResetPasswordPage />} />
        <Route path="/resetpassword/success" element={<ResetPasswordSuccessPage />} />

      <Route element={<RequireAuth />}>
        <Route path="/dashboard" element={<Navigate to="/dashboard/35KBoSgoAK" replace />} />
        <Route path="/dashboard/:id" element={<DashboardPage />} />
        <Route path="/form/sHAnZphf69" element={<SignYourselfPage />} />
        <Route path="/form/8mZzFxbG1z" element={<RequestSignPage />} />
        <Route path="/form/template" element={<TemplateCreatePage />} />
        <Route path="/form/:id" element={<SectionWorkspacePage sectionKey="form" />} />
        <Route path="/report/:id" element={<SectionWorkspacePage sectionKey="report" />} />
        <Route path="/drive" element={<SectionWorkspacePage sectionKey="drive" />} />
        <Route path="/managesign" element={<AccountSettingsPage mode="signature" />} />
        <Route path="/users" element={<AccountSettingsPage mode="users" />} />
        <Route path="/preferences" element={<AccountSettingsPage mode="preferences" />} />
        <Route path="/generatetoken" element={<AccountSettingsPage mode="api" />} />
        <Route path="/profile" element={<AccountSettingsPage mode="profile" />} />
        <Route path="/profile/preferences" element={<AccountSettingsPage mode="user-preferences" />} />
        <Route path="/changepassword" element={<AccountSettingsPage mode="password" />} />
        <Route path="/branding" element={<AccountSettingsPage mode="branding" />} />
        <Route path="/admin/certificates" element={<CertificateManagementPage />} />
        <Route path="/template/:templateId" element={<SectionWorkspacePage sectionKey="template" />} />
        <Route path="/signaturePdf/:docId" element={<IndividualSigningPage />} />
        <Route
          path="/requestSignPdf/:docId"
          element={<IndividualSigningPage editorMode="request" backTo="/form/8mZzFxbG1z" />}
        />
        <Route path="/signaturePdf/:docId/completed" element={<SignedDocumentSummaryPage />} />
        <Route path="/internalSign/:docId" element={<InternalSignerPage />} />
        <Route path="/draftDocument" element={<SectionWorkspacePage sectionKey="draftDocument" />} />
        <Route path="/placeHolderSign/:docId" element={<SectionWorkspacePage sectionKey="placeHolderSign" />} />
      </Route>

      <Route path="/success" element={<SuccessPage />} />

      {/* External signer routes (no authentication required) */}
      <Route path="/load/recipientSignPdf/:docId/:contactBookId" element={<ExternalSignerPage />} />
      <Route path="/recipientSignPdf/:docId/:contactBookId" element={<ExternalSignerPage />} />
      <Route path="/recipientSignPdf/:docId" element={<ExternalSignerPage />} />
      <Route path="/recipientSignPdf" element={<SignatureGatewayPage />} />
      <Route path="/pdfRequestFiles" element={<SignatureGatewayPage />} />
      <Route path="/pdfRequestFiles/:docId" element={<ExternalSignerPage />} />

      <Route
        path="/load/template/:templateId"
        element={
          <LegacyRedirect
            title={t("new-ui.legacy.open-template-flow", { defaultValue: "Opening template flow" })}
          />
        }
      />
      <Route
        path="/load/placeholdersign/:docId"
        element={
          <LegacyRedirect
            title={t("new-ui.legacy.open-placeholder-flow", { defaultValue: "Opening placeholder flow" })}
          />
        }
      />

      {/* Guest login routes */}
      <Route path="/login/:id/:userMail/:contactBookId/:serverUrl" element={<GuestLoginPage />} />
      <Route path="/login/:base64url" element={<GuestLoginPage />} />

      <Route
        path="*"
        element={
          <LegacyRedirect
            title={t("new-ui.legacy.open-legacy-route", { defaultValue: "Opening legacy route" })}
          />
        }
      />
    </Routes>
  </BrowserRouter>
  );
}

function App() {
  return (
    <AlertProvider>
      <AppContent />
    </AlertProvider>
  );
}

export default App;
